import { AlertCircle } from "lucide-react";
import { format, parseISO } from "date-fns";
import { formatPrice, cn } from "../../lib/utils";
import type { EventPricePoint } from "../../lib/types";
import { Badge } from "../ui/Badge";

function CategoryAvailabilityBadges({
  availability,
}: {
  availability: EventPricePoint["availability"];
}) {
  if (availability === "full") {
    return (
      <Badge variant="error" size="sm" className="shrink-0 self-start">
        Fully Booked
      </Badge>
    );
  }
  if (availability === "nearly_full") {
    return (
      <Badge variant="warning" size="sm" className="shrink-0 self-start">
        Limited Places Remaining
      </Badge>
    );
  }
  return null;
}

interface PricePointRowProps {
  pricePoint: EventPricePoint;
  isFuture: boolean;
  bookingEnd: Date;
  hasAnyNotes: boolean;
  hasFuturePricePoints: boolean;
}

function PricePointRow({
  pricePoint,
  isFuture,
  bookingEnd,
  hasAnyNotes,
  hasFuturePricePoints,
}: PricePointRowProps) {
  const availableFrom = pricePoint.availableFrom
    ? parseISO(pricePoint.availableFrom)
    : null;
  const expiresBeforeBookingCloses = pricePoint.availableUntil
    ? new Date(pricePoint.availableUntil) < bookingEnd
    : false;
  const expiresAt = pricePoint.availableUntil
    ? new Date(pricePoint.availableUntil)
    : null;

  const isFull = pricePoint.availability === "full";

  return (
    <tr
      className={`border-b border-gray-100 ${isFuture ? "opacity-60" : ""}`}
    >
      <td className="py-2 px-3 align-top">
        <div className="flex justify-between items-start gap-2">
          <div
            className={`min-w-0 flex-1 font-medium ${
              isFuture ? "text-gray-700" : "text-gray-900"
            }`}
          >
            {pricePoint.label}
          </div>
          <CategoryAvailabilityBadges availability={pricePoint.availability} />
        </div>
        {pricePoint.description && (
          <div
            className={`text-sm mt-2 w-full max-w-none ${
              isFuture ? "text-gray-500" : "text-gray-600"
            }`}
          >
            {pricePoint.description}
          </div>
        )}
      </td>
      <td className="py-2 px-3 align-top">
        <div>
          <span
            className={cn(
              "font-semibold",
              isFuture ? "text-gray-700" : "text-gray-900",
              isFull && "line-through text-gray-500",
            )}
          >
            {formatPrice(pricePoint.priceCents, pricePoint.currency)}
          </span>
        </div>
        {pricePoint.depositCents &&
          pricePoint.balanceDueAt &&
          parseISO(pricePoint.balanceDueAt) > new Date() && (
            <div
              className={`text-xs mt-0.5 space-y-0.5 ${
                isFuture ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <div>
                {formatPrice(pricePoint.depositCents, pricePoint.currency)}{" "}
                {isFuture ? "deposit due when available" : "deposit due now"}
              </div>
              <div>
                {formatPrice(
                  pricePoint.priceCents - pricePoint.depositCents,
                  pricePoint.currency,
                )}{" "}
                due on {format(parseISO(pricePoint.balanceDueAt), "MMM do, yyyy")}
              </div>
            </div>
          )}
      </td>
      {(hasAnyNotes || hasFuturePricePoints) && (
        <td className="py-2 px-3">
          {isFuture ? (
            <div className="flex items-center gap-1 text-sm text-gray-600">
              <AlertCircle className="w-4 h-4" />
              <span>
                {availableFrom
                  ? `From ${format(availableFrom, "MMM do, yyyy h:mm a")}`
                  : "Available soon"}
                {expiresBeforeBookingCloses && expiresAt
                  ? ` until ${format(expiresAt, "MMM do, yyyy h:mm a")}`
                  : null}
              </span>
            </div>
          ) : expiresBeforeBookingCloses && expiresAt ? (
            <div className="flex items-center gap-1 text-sm text-yellow-700">
              <AlertCircle className="w-4 h-4" />
              <span>
                Price available until {format(expiresAt, "MMM do, yyyy h:mm a")}
              </span>
            </div>
          ) : null}
        </td>
      )}
    </tr>
  );
}

interface ProcessedPricePoint {
  pp: EventPricePoint;
  isFuture: boolean;
}

interface EventPriceTableProps {
  groupedByLabel: Map<string, ProcessedPricePoint[]>;
  hasAnyNotes: boolean;
  hasFuturePricePoints: boolean;
  bookingEnd: Date;
}

export function EventPriceTable({
  groupedByLabel,
  hasAnyNotes,
  hasFuturePricePoints,
  bookingEnd,
}: EventPriceTableProps) {
  return (
    <div className="pt-4">
      <h3 className="font-semibold text-gray-900 mb-3">Pricing</h3>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700">
                Category
              </th>
              <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700">
                Price
              </th>
              {(hasAnyNotes || hasFuturePricePoints) && (
                <th className="text-left py-2 px-3 text-sm font-semibold text-gray-700">
                  Notes
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {Array.from(groupedByLabel.values()).map((group) =>
              group.map(({ pp, isFuture }) => (
                <PricePointRow
                  key={pp.id}
                  pricePoint={pp}
                  isFuture={isFuture}
                  bookingEnd={bookingEnd}
                  hasAnyNotes={hasAnyNotes}
                  hasFuturePricePoints={hasFuturePricePoints}
                />
              )),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
